<template>
  <div
    class="relative"
    :class="{
      '[&_#drop-hover]:bg-grey-975-hover/30': dragging,
    }"
    @drop.prevent="drop"
    @dragover.prevent="dragover"
    @dragleave.prevent.stop="dragleave"
    ref="dropzone"
  >
    <slot></slot>
    <button
      class="sr-only focus:not-sr-only text-left"
      @keydown.enter.prevent="onClick"
      @click.prevent="onClick"
      :tabindex="0"
      :disabled="disabled"
      data-sentry-id="DropfileChooseFilesBtn"
    >
      <div class="p-1">Choisir les fichiers à téléverser</div>
    </button>

    <!-- the file input is for screen reader users -->
    <input
      v-if="forceInput"
      ref="fileInput"
      type="file"
      @change="onFiles"
      class="hidden"
      :accept="accept ? accept : acceptedMimeTypes.join(',')"
      multiple
      data-sentry-id="DropfileInput"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import { useFileStore } from "../../../stores/file.store";

interface Props {
  disabled?: boolean;
  accept?: string;
}
const props = withDefaults(defineProps<Props>(), { disabled: false });

const emit = defineEmits<{
  (e: "input", value: File[]): void;
  (e: "dragging", value: boolean): void;
}>();

const fileStore = useFileStore();
const acceptedMimeTypes = computed(() => fileStore.accepted.mimeTypes);
const forceInput = ref(true);

const dropzone = ref<HTMLElement | null>(null);
const dragging = ref<boolean>(false);
function updateDragging(value: boolean) {
  if (dragging.value !== value) emit("dragging", value);
  dragging.value = value;
}

function getFiles(event: DragEvent) {
  // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/File_drag_and_drop#process_the_drop
  if (event.dataTransfer?.items) {
    return [...event.dataTransfer.items]
      .filter(file => file.kind === "file")
      .map(item => {
        return item.getAsFile()!;
      });
  }
  return [...event.dataTransfer!.files].map(file => {
    return file;
  });
}

function hasFiles(event: DragEvent): boolean {
  return getFiles(event).length > 0;
}

function drop(event: DragEvent) {
  if (props.disabled) return false;
  updateDragging(false);
  if (hasFiles(event)) emit("input", getFiles(event));
}

function dragover(event: DragEvent) {
  if (props.disabled) return false;
  // If there are any performance issues during dragging, this is the culprit
  if (hasFiles(event)) updateDragging(true);
}

function dragleave(event: DragEvent) {
  if (props.disabled) return false;
  if (dropzone.value?.contains(event.relatedTarget as Node)) return;
  updateDragging(false);
}

// For Screen Readers we use an alternative file input rather than the drop files mechanism
const fileInput = ref<HTMLInputElement>();
function onClick() {
  fileInput.value!.click();
}

async function onFiles() {
  if (fileInput.value?.files) {
    emit("input", Array.from(fileInput.value.files));
    forceInput.value = false;
    await nextTick();
    forceInput.value = true;
  }
}
</script>
